import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import enTranslations from './translations/en.json';

// Type assertion for JSON import
const translations = enTranslations as Record<string, any>;

type TranslationKey = string;
type TranslationParams = Record<string, string | number>;

interface TranslationContextType {
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const getNestedValue = (obj: any, path: string): string | undefined => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const replaceParams = (text: string, params?: TranslationParams): string => {
  if (!params) return text;
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)),
    text
  );
};

interface TranslationProviderProps {
  children: ReactNode;
  locale?: string;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children,
  locale = 'en',
}) => {

  const t = useCallback((key: TranslationKey, params?: TranslationParams): string => {
    const translation = getNestedValue(translations, key);
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return replaceParams(translation, params);
  }, []);

  const value = useMemo(() => ({ t }), [t]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

